import parse, { DOMNode, Element, HTMLReactParserOptions, domToReact } from 'html-react-parser';
import { ComponentPropsWithoutRef, FC, ReactNode } from 'react';
import customTwMerge from 'twMerge';

import Bullet from 'molecules/Bullet';
import Link from 'molecules/Link';
import { headingStyles } from 'molecules/RichText/styles';
import { isEmptyBr } from 'molecules/RichText/utils';

import { hasArrayValues } from 'utils/array/arrays';
import { toKebabCase } from 'utils/strings';

interface RichTextProps extends ComponentPropsWithoutRef<'div'> {
  text: string;
  isBullet?: boolean;
  isBulletStandard?: boolean;
  afterFirstParagraph?: ReactNode;
  isFormContent?: boolean;
  unwrapFirstParagraph?: boolean;
}

const options = (
  isBullet?: boolean,
  afterFirstParagraph?: ReactNode,
  isBulletStandard?: boolean,
  isFormContent?: boolean,
  unwrapFirstParagraph?: boolean,
): HTMLReactParserOptions => {
  let paragraphCount = 0;

  return {
    replace(node) {
      if (!('name' in node) || !(node instanceof Element)) {
        return null;
      }

      const childNodes = node.children as DOMNode[];
      if (!hasArrayValues(childNodes || isEmptyBr(childNodes))) {
        return <></>;
      }

      switch (node.name) {
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6': {
          const content = domToReact(
            childNodes,
            options(isBullet, undefined, isBulletStandard, isFormContent, unwrapFirstParagraph),
          );
          const stringContent = typeof content === 'string' ? content : '';
          const id = toKebabCase(stringContent);

          let hClass = 'pt-4 first:pt-0';
          if (isFormContent) {
            hClass += ' text-xl';
          }

          return (
            <node.name id={id} className={`${headingStyles({ type: node.name })} ${hClass}`}>
              {content}
            </node.name>
          );
        }
        case 'a':
          return (
            <Link
              className="font-roboto text-primary-1000 underline transition-colors focus-outline-primary hover:text-primary-1000 dark:text-primary-300 dark:hover:text-primary-300"
              href={node.attribs.href}
              aria-label={`Link to ${node.attribs.href}`}
            >
              {domToReact(
                childNodes,
                options(isBullet, afterFirstParagraph, isBulletStandard, isFormContent, unwrapFirstParagraph),
              )}
            </Link>
          );
        case 'ul': {
          const ulClass = isBulletStandard
            ? 'list-disc pl-6 flex flex-col gap-2 sm:gap-3 marker:text-gray-1100'
            : 'flex flex-col gap-2 sm:gap-3';

          return (
            <ul className={ulClass}>
              {domToReact(
                childNodes,
                options(isBullet, afterFirstParagraph, isBulletStandard, isFormContent, unwrapFirstParagraph),
              )}
            </ul>
          );
        }
        case 'ol':
          return (
            <ol className="flex list-decimal flex-col gap-2 pl-6 sm:gap-3">
              {domToReact(
                childNodes,
                options(isBullet, afterFirstParagraph, isBulletStandard, isFormContent, unwrapFirstParagraph),
              )}
            </ol>
          );
        case 'li': {
          const parentName = (node.parent as Element | undefined)?.name;

          if (parentName === 'ul') {
            if (isBulletStandard) {
              return (
                <li className="text-sm leading-6 sm:text-md">
                  {domToReact(
                    childNodes,
                    options(isBullet, afterFirstParagraph, isBulletStandard, isFormContent, unwrapFirstParagraph),
                  )}
                </li>
              );
            }

            return (
              <Bullet isBullet={isBullet}>
                {domToReact(
                  childNodes,
                  options(isBullet, afterFirstParagraph, isBulletStandard, isFormContent, unwrapFirstParagraph),
                )}
              </Bullet>
            );
          }

          return (
            <li className="text-sm leading-6 sm:text-md">
              {domToReact(
                childNodes,
                options(isBullet, afterFirstParagraph, isBulletStandard, isFormContent, unwrapFirstParagraph),
              )}
            </li>
          );
        }
        case 'span': {
          const { style: rawStyle, ...restAttribs } = node.attribs;
          const styleObj: Record<string, string> = {};

          if (rawStyle) {
            rawStyle
              .split(';')
              .filter(Boolean)
              .forEach(rule => {
                const [prop, ...valParts] = rule.split(':');
                const propName = prop?.trim();
                const value = valParts.join(':').trim();
                if (propName && value && propName !== 'font-size') {
                  const camelCase = propName.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
                  styleObj[camelCase] = value;
                }
              });
          }

          return (
            <span
              {...(restAttribs as React.HTMLAttributes<HTMLSpanElement>)}
              style={Object.keys(styleObj).length > 0 ? styleObj : undefined}
            >
              {domToReact(
                childNodes,
                options(isBullet, afterFirstParagraph, isBulletStandard, isFormContent, unwrapFirstParagraph),
              )}
            </span>
          );
        }
        case 'code':
          return (
            <code className="inline-block bg-primary-50 px-3 py-1.5 font-robotoMono text-primary-900">
              {domToReact(
                childNodes,
                options(isBullet, afterFirstParagraph, isBulletStandard, isFormContent, unwrapFirstParagraph),
              )}
            </code>
          );
        case 'p': {
          paragraphCount++;

          // If unwrapFirstParagraph is true and this is the first paragraph, render the
          // content without a <p> wrapper, but keep it in ONE block-level element: the
          // RichText root is a flex column, and bare children (text, links, <em>...)
          // would each become their own flex item, breaking the paragraph onto
          // separate lines whenever it contains any inline formatting.
          if (unwrapFirstParagraph && paragraphCount === 1) {
            return (
              <>
                <span className="block">
                  {domToReact(
                    childNodes,
                    options(isBullet, afterFirstParagraph, isBulletStandard, isFormContent, unwrapFirstParagraph),
                  )}
                </span>
                {afterFirstParagraph ? afterFirstParagraph : null}
              </>
            );
          }

          let pClass = '';

          if (isFormContent) {
            pClass = paragraphCount === 1 ? 'text-xl leading-normal' : 'text-md leading-6';
          }

          return (
            <>
              <p className={pClass}>
                {domToReact(
                  childNodes,
                  options(isBullet, afterFirstParagraph, isBulletStandard, isFormContent, unwrapFirstParagraph),
                )}
              </p>
              {paragraphCount === 1 && afterFirstParagraph ? afterFirstParagraph : null}
            </>
          );
        }

        default:
          return node;
      }
    },
  };
};

const RichText: FC<RichTextProps> = ({
  text,
  className,
  isBullet,
  isBulletStandard,
  afterFirstParagraph,
  isFormContent = false,
  unwrapFirstParagraph = false,
}) => {
  if (typeof text !== 'string' || !text) {
    return null;
  }

  const gapClass = isFormContent ? 'gap-1' : 'gap-4';

  return (
    <div className={customTwMerge(`flex flex-col ${gapClass} text-rich-body`, className)}>
      {parse(text, options(isBullet, afterFirstParagraph, isBulletStandard, isFormContent, unwrapFirstParagraph))}
    </div>
  );
};

export default RichText;
